import { useEffect, useRef, useState } from "react";

import { cn, isNotEmpty } from "@/libs/utils";

import { ValueType } from "@/types/bid";

import { GetLiveTextSearch } from "@/services/Bid";

import { BidNoticeSuggestSearchClientList } from "@/consts/Bid";
import { DashboardSearchProjectByTitle } from "@/consts/Dashboard";

import CommonInput from "@/components/Common/Input";
import CommonPlusButton from "@/components/Common/PlusButton";
import KeywordItem from "@/components/KeywordItem";

type Props = {
  placeholder: string;
  method: string;
  defaultValue: ValueType[] | [];
  onChange: (valueList: ValueType[]) => void;
  itemKey: string;
  badgeColor: string;
  showPlusButton?: boolean;
  enableEnterKey?: boolean;
};

const OnlyNameList = [
  BidNoticeSuggestSearchClientList,
  DashboardSearchProjectByTitle,
];

export default function BidLiveTextSearch({
  placeholder,
  method,
  defaultValue,
  onChange,
  itemKey,
  badgeColor,
  showPlusButton = true,
  enableEnterKey = true,
}: Props) {
  const [height, setHeight] = useState<number>(28);
  const ref = useRef<any>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [searchResult, setSearchResult] = useState<ValueType[]>([]);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hideResultTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSelectingRef = useRef<boolean>(false);

  const handleLiveSearch = (value: string) => {
    setShowDropdown(true);
    try {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = setTimeout(async () => {
        const result = await GetLiveTextSearch(method, value);
        if (result.data.success) {
          setSearchResult(
            result.data.pods[0].content.items.map((item: any) => ({
              label: item[`${itemKey}_name`],
              value: item[`${itemKey}_id`],
            }))
          );
        } else {
          setSearchResult([]);
        }
      }, 100);
    } catch (e) {
      setSearchResult([]);
    } finally {
    }
  };

  const handleChange = (newSelectList: ValueType[]) => {
    onChange(newSelectList);
    setHeight(ref.current?.clientHeight || 28);
  };

  const handleClickPlus = () => {
    if (isNotEmpty(searchText)) {
      const newItem = { label: searchText, value: searchText };
      const newSelectList = defaultValue.some(
        (prevItem) => prevItem.value === newItem.value
      )
        ? [...defaultValue]
        : [...defaultValue, newItem];
      handleChange(newSelectList);
      setSearchResult([]);
      setShowDropdown(false);
      setSearchText("");
    }
  };

  const handleResultClick = (result: ValueType) => {
    isSelectingRef.current = true;
    const newSelectList = defaultValue.some(
      (prevItem) => prevItem.value === result.value
    )
      ? [...defaultValue]
      : [...defaultValue, result];
    handleChange(newSelectList);
    setSearchResult([]);
    setShowDropdown(false);
    setSearchText("");
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 0);
  };

  // 전체 선택 기능
  const handleSelectAll = () => {
    isSelectingRef.current = true;
    // 중복 제거하면서 모든 검색 결과 추가
    const existingValues = new Set(defaultValue.map(item => item.value));
    const newItems = searchResult.filter(item => !existingValues.has(item.value));
    const newSelectList = [...defaultValue, ...newItems];
    handleChange(newSelectList);
    setSearchResult([]);
    setShowDropdown(false);
    setSearchText("");
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 0);
  };

  const handleDeleteItem = (index: number) => {
    const newSelectList = [...defaultValue.toSpliced(index, 1)];
    handleChange(newSelectList);
  };

  const handleBlur = () => {
    if (isSelectingRef.current) {
      return;
    }

    if (hideResultTimeoutRef.current) {
      clearTimeout(hideResultTimeoutRef.current);
    }
    hideResultTimeoutRef.current = setTimeout(() => {
      setSearchResult([]);
      setShowDropdown(false);
    }, 150);
  };

  useEffect(() => {
    if (ref.current) {
      setHeight(ref.current.clientHeight);
    }
  }, [defaultValue]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (hideResultTimeoutRef.current) {
        clearTimeout(hideResultTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative flex gap-[8px]" onBlur={handleBlur}>
      <CommonInput
        className="min-w-[180px]"
        placeholder={placeholder}
        defaultValue={searchText}
        onChange={(value) => {
          setSearchText(value);
          handleLiveSearch(value);
        }}
        onKeyDownEnter={enableEnterKey ? () => {
          handleClickPlus();
        } : undefined}
        onFocus={() => {
          if (hideResultTimeoutRef.current) {
            clearTimeout(hideResultTimeoutRef.current);
          }
          // 빈 상태에서도 전체 목록을 보여주기 위해 빈 문자열로 검색
          handleLiveSearch(searchText || "");
        }}
      />
      {showPlusButton && <CommonPlusButton onClick={handleClickPlus} />}
      <div className="flex gap-[8px] overflow-x-auto">
        {defaultValue.map((item, index) => (
          <KeywordItem
            key={index}
            index={index}
            label={
              item.label +
              (!OnlyNameList.includes(method) && item.label !== item.value
                ? `(${item.value})`
                : "")
            }
            className={badgeColor}
            onDelete={() => handleDeleteItem(index)}
          />
        ))}
      </div>
      {showDropdown && (
        <div
          ref={ref}
          className={cn(
            "max-h-[120px] w-[180px] overflow-auto absolute z-10 bg-white p-[8px] flex flex-col gap-[4px] border-x-[1px] border-b-[1px] rounded-b-[4px] text-[10px] font-bold"
          )}
          style={{
            top: `${height + 2}px`,
          }}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
        >
          {searchResult.length > 0 ? (
            <>
              {/* 전체 선택 버튼 */}
              <div
                className="relative p-[4px] mb-1 bg-blue-50 hover:bg-blue-100 rounded-[4px] cursor-pointer pointer-events-auto text-center text-blue-600 border-b border-gray-200"
                onClick={handleSelectAll}
              >
                전체 선택 ({searchResult.length}개)
              </div>
              {searchResult.map((result, resultIndex) => (
                <div
                  key={resultIndex}
                  className="relative p-[4px] hover:bg-gray-D rounded-[4px] cursor-pointer pointer-events-auto"
                  onClick={() => handleResultClick(result)}
                >
                  {result.label}
                  {!OnlyNameList.includes(method) &&
                    result.label !== result.value &&
                    `(${result.value})`}
                </div>
              ))}
            </>
          ) : (
            <div className="relative p-[4px] text-gray-C text-center">
              검색 결과가 없습니다
            </div>
          )}
        </div>
      )}
    </div>
  );
}
